import MirrorBackButton from "@/components/MirrorBackButton";
import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useMotionValue, useTransform, animate } from "framer-motion";
import { toast } from "sonner";
import {
  ChevronLeft, Apple, Star, ArrowRight, Droplets, X, Loader2, RefreshCw,
  Mic, MicOff, Plus, Flame, Zap, TrendingUp, Utensils, GlassWater,
  MoreVertical, Pencil, Trash2, Volume2, History,
} from "lucide-react";
import MobileLayout from "@/components/MobileLayout";
import { useLanguage } from "@/i18n/LanguageProvider";
import { edgeLocaleFor } from "@/i18n/locale";
import { usePremium } from "@/contexts/PremiumContext";
import { analytics } from "@/lib/analytics";
import { InfoTooltip } from "@/components/InfoTooltip";
import { useNutrition, nutritionActionId, MealType, type NutritionLog } from "@/hooks/useNutrition";
import { ModuleHistoryList } from "@/components/history/ModuleHistoryList";
import { AnalyticsDashboard } from "@/components/analytics/AnalyticsDashboard";
import { buildDashboard } from "@/lib/analytics/dashboard";
import { NUTRITION_DASHBOARD, extractNutritionSamples } from "@/lib/analytics/nutritionDashboard";
import type { Granularity } from "@/lib/analytics/engine";
import { useVoiceInput } from "@/hooks/useVoiceInput";
import FoodSearchScreen from "@/components/nutrition/FoodSearchScreen";
import NutritionMiniMirror from "@/components/nutrition/NutritionMiniMirror";
import PhotoCaptureScreen from "@/components/nutrition/PhotoCaptureScreen";
import { Camera } from "lucide-react";
import type { LocalFood } from "@/data/brazilianFoods";
import { supabase } from "@/integrations/supabase/client";
import { useNutritionResource } from "@/hooks/useNutritionResource";
import { useNutritionReact } from "@/hooks/useNutritionReact";
import ReportContentButton from "@/components/report/ReportContentButton";
import { REPORT_SURFACES } from "@/lib/report/surfaces";
import { useReportedContent } from "@/contexts/ReportedContentContext";

// ═══════════════════════════════════════════════════════════════════════════
// VYV NUTRITION — PREMIUM PERSONAL FUEL INTELLIGENCE
// ═══════════════════════════════════════════════════════════════════════════

// TODO: Week 3 — Connect to Claude API mirror brief endpoint
const MIRROR_INSIGHT_ENABLED = false;

// ── VYV Floating Particles ──────────────────────────────────────────────

const VYVParticles: React.FC = React.memo(() => {
  const particles = React.useMemo(() =>
    Array.from({ length: 18 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 2.5 + 1,
      duration: Math.random() * 12 + 18,
      delay: Math.random() * 8,
      color: i % 3 === 0
        ? "rgba(0, 229, 255, 0.35)"
        : i % 3 === 1
          ? "rgba(139, 92, 246, 0.25)"
          : "rgba(0, 229, 255, 0.18)",
    })),
  []);

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute rounded-full"
          style={{
            width: p.size,
            height: p.size,
            left: `${p.x}%`,
            top: `${p.y}%`,
            background: p.color,
            boxShadow: `0 0 ${p.size * 3}px ${p.color}`,
          }}
          animate={{
            y: [0, -30, 10, -20, 0],
            x: [0, 15, -10, 8, 0],
            opacity: [0.45, 0.85, 0.5, 0.95, 0.45],
            scale: [1, 1.3, 0.8, 1.2, 1],
          }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
});
VYVParticles.displayName = "VYVParticles";

// ── Breathing Glow Orbs ─────────────────────────────────────────────────

const BreathingOrbs: React.FC = React.memo(() => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
    <motion.div
      className="absolute -top-20 -right-20 w-[300px] h-[300px] rounded-full"
      style={{ background: "radial-gradient(circle, rgba(0,229,255,0.06) 0%, transparent 70%)" }}
      animate={{ opacity: [0.3, 0.7, 0.3], scale: [0.95, 1.08, 0.95] }}
      transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.div
      className="absolute -bottom-32 -left-24 w-[280px] h-[280px] rounded-full"
      style={{ background: "radial-gradient(circle, rgba(139,92,246,0.05) 0%, transparent 70%)" }}
      animate={{ opacity: [0.2, 0.55, 0.2], scale: [1, 1.12, 1] }}
      transition={{ duration: 10, delay: 2, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.div
      className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[400px] h-[200px] rounded-full"
      style={{ background: "radial-gradient(ellipse 60% 40% at 50% 50%, rgba(0,229,255,0.03), transparent)" }}
      animate={{ opacity: [0.15, 0.4, 0.15] }}
      transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
    />
  </div>
));
BreathingOrbs.displayName = "BreathingOrbs";

// ── CSS Keyframes ──────────────────────────────────────────────────────

const NutritionKeyframes: React.FC = React.memo(() => (
  <style>{`
    @keyframes pulse { 0%, 100% { opacity: 0.4; transform: scale(1); } 50% { opacity: 0.8; transform: scale(1.15); } }
    @keyframes waterRipple { 0% { transform: scale(1); opacity: 1; } 100% { transform: scale(2.5); opacity: 0; } }
    @keyframes cupFill { 0% { transform: translateY(100%); } 100% { transform: translateY(0%); } }
    @keyframes floatUp { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-6px); } }
  `}</style>
));
NutritionKeyframes.displayName = "NutritionKeyframes";

// ── Ambient Orbs (behind content) ──────────────────────────────────────

const AmbientOrbs: React.FC = React.memo(() => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
    <div style={{
      position: "absolute", top: "-10%", left: "20%", width: 300, height: 300, borderRadius: "50%",
      background: "radial-gradient(circle, hsl(187 72% 50% / 0.06) 0%, transparent 70%)",
      filter: "blur(40px)", animation: "pulse 6s ease-in-out infinite",
    }} />
    <div style={{
      position: "absolute", bottom: "20%", right: "-10%", width: 250, height: 250, borderRadius: "50%",
      background: "radial-gradient(circle, hsl(270 72% 50% / 0.05) 0%, transparent 70%)",
      filter: "blur(40px)", animation: "pulse 8s ease-in-out infinite reverse",
    }} />
  </div>
));
AmbientOrbs.displayName = "AmbientOrbs";

// ── Animated Counter ────────────────────────────────────────────────────

const AnimatedNumber: React.FC<{ value: number; duration?: number }> = ({ value, duration = 1.2 }) => {
  const motionVal = useMotionValue(0);
  const rounded = useTransform(motionVal, (v) => Math.round(v));
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    const controls = animate(motionVal, value, { duration, ease: "easeOut" });
    const unsubscribe = rounded.on("change", (v) => setDisplay(v));
    return () => { controls.stop(); unsubscribe(); };
  }, [value, duration, motionVal, rounded]);

  return <>{display}</>;
};

// ── Radial Macro Ring ───────────────────────────────────────────────────

const MacroRing: React.FC<{
  value: number; max: number; color: string; glowColor: string;
  label: string; unit: string; icon: React.ReactNode;
}> = ({ value, max, color, glowColor, label, unit, icon }) => {
  const r = 32;
  const circ = 2 * Math.PI * r;
  const pct = Math.min(value / max, 1);

  return (
    <div className="flex flex-col items-center relative">
      <motion.div
        className="absolute top-1 w-[68px] h-[68px] rounded-full"
        style={{ background: `radial-gradient(circle, ${glowColor}, transparent 70%)` }}
        animate={{ opacity: [0.15, 0.4, 0.15] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      />
      <svg width="80" height="80" viewBox="0 0 80 80" className="relative z-10" style={{ filter: `drop-shadow(0 0 6px ${glowColor})` }}>
        <circle cx="40" cy="40" r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="4" />
        <motion.circle
          cx="40" cy="40" r={r} fill="none" stroke={color} strokeWidth="5"
          strokeLinecap="round" strokeDasharray={circ}
          initial={{ strokeDashoffset: circ }}
          animate={{ strokeDashoffset: circ - pct * circ }}
          transition={{ delay: 0.3, duration: 1.2, ease: "easeOut" }}
          transform="rotate(-90 40 40)"
          style={{ filter: `drop-shadow(0 0 8px ${glowColor})` }}
        />
        <foreignObject x="22" y="22" width="36" height="36">
          <div className="w-full h-full flex items-center justify-center" style={{ color }}>{icon}</div>
        </foreignObject>
      </svg>
      <div className="mt-1.5 text-center">
        <p className="text-[13px] font-display font-semibold text-white leading-none"><AnimatedNumber value={value} /></p>
        <p className="text-[8px] font-display tracking-widest mt-0.5" style={{ color: "hsl(187 72% 50% / 0.55)" }}>/{max}{unit}</p>
        <p className="text-[9px] font-display tracking-wide mt-0.5" style={{ color, opacity: 0.9 }}>{label}</p>
      </div>
    </div>
  );
};

// ── Calorie Arc (main hero) ─────────────────────────────────────────────

const CalorieArc: React.FC<{ current: number; target: number }> = ({ current, target }) => {
  const pct = Math.min(current / target, 1);
  const r = 58;
  const circ = 2 * Math.PI * r;
  const remaining = target - current;

  return (
    <motion.div className="relative flex flex-col items-center" whileHover={{ scale: 1.02 }} transition={{ type: "spring", stiffness: 300 }}>
      <motion.div
        className="absolute top-0 w-[140px] h-[140px] rounded-full"
        style={{ background: "radial-gradient(circle, rgba(0,229,255,0.10), transparent 65%)" }}
        animate={{ opacity: [0.3, 0.7, 0.3], scale: [0.98, 1.08, 0.98] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      />
      <svg width="140" height="140" viewBox="0 0 140 140" className="relative z-10" style={{ filter: "drop-shadow(0 0 8px hsl(187 72% 50% / 0.4))" }}>
        <circle cx="70" cy="70" r={r} fill="none" stroke="hsl(187 72% 50% / 0.12)" strokeWidth="6" />
        <motion.circle
          cx="70" cy="70" r={r} fill="none" stroke="url(#calorieGradient)" strokeWidth="8"
          strokeLinecap="round" strokeDasharray={circ}
          initial={{ strokeDashoffset: circ }}
          animate={{ strokeDashoffset: circ - pct * circ }}
          transition={{ delay: 0.2, duration: 1.5, ease: "easeOut" }}
          transform="rotate(-90 70 70)"
          style={{ filter: "drop-shadow(0 0 12px rgba(0,229,255,0.5))" }}
        />
        <defs>
          <linearGradient id="calorieGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="hsl(187 72% 60%)" />
            <stop offset="50%" stopColor="hsl(200 72% 55%)" />
            <stop offset="100%" stopColor="hsl(220 72% 65%)" />
          </linearGradient>
        </defs>
        <text x="70" y="65" textAnchor="middle" fill="white" fontSize="32" fontWeight="700" fontFamily="'Sora', sans-serif">
          <AnimatedNumber value={current} />
        </text>
        <text x="70" y="82" textAnchor="middle" fill="hsl(187, 72%, 60%, 0.70)" fontSize="9" fontFamily="'Sora', sans-serif" letterSpacing="0.1em">
          / {target} kcal
        </text>
      </svg>
      <motion.p
        className="text-[10px] font-display mt-2"
        style={{ color: remaining > 0 ? "hsl(187 72% 60%)" : "rgba(139,92,246,0.7)", textShadow: remaining > 0 ? "0 0 12px hsl(187 72% 50% / 0.5)" : undefined, animation: remaining > 0 ? "floatUp 3s ease-in-out infinite" : undefined }}
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}
      >
        {remaining > 0 ? `${remaining} remaining` : "Target reached"}
      </motion.p>
    </motion.div>
  );
};

// ── Hydration Tracker ───────────────────────────────────────────────────

const HydrationTracker: React.FC<{
  cups: number; goal: number; saving: boolean; onAdd: () => void; t: (k: string) => string; tf: (key: string, values: Record<string, string | number>) => string;
}> = ({ cups: rawCups, goal, saving, onAdd, t, tf }) => {
  const maxCups = Math.max(1, goal);
  const cups = Math.max(0, rawCups ?? 0);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl p-4 relative overflow-hidden"
      style={{
        background: "linear-gradient(180deg, hsl(200 30% 8% / 0.95) 0%, hsl(200 50% 10% / 0.90) 100%)",
        border: "1px solid hsl(200 70% 50% / 0.30)", backdropFilter: "blur(20px)",
      }}
    >
      <div className="absolute top-0 left-0 right-0 h-[1px]"
        style={{ background: "linear-gradient(90deg, transparent, hsl(200 70% 55% / 0.30), transparent)" }} />
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <motion.div
            className="w-8 h-8 rounded-xl flex items-center justify-center"
            style={{ background: "hsl(200 70% 50% / 0.10)", border: "1px solid hsl(200 70% 50% / 0.25)" }}
            animate={{ boxShadow: ["0 0 0px hsl(200 90% 60% / 0)", "0 0 14px hsl(200 90% 60% / 0.20)", "0 0 0px hsl(200 90% 60% / 0)"] }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            <GlassWater size={14} style={{ color: "hsl(200 90% 60%)" }} />
          </motion.div>
          <div>
            <p className="text-[10px] font-display tracking-widest uppercase" style={{ color: "hsl(200 70% 60% / 0.80)" }}>
              {t("nutrition.hydration")}
            </p>
            <p className="text-[11px] font-body font-semibold" style={{ color: "hsl(200 90% 65%)" }}>{tf("nutritionHydrationAction.registeredCups", { cups, goal: maxCups })}</p>
          </div>
        </div>
        <motion.button disabled={saving || cups >= maxCups} aria-label={t("nutrition.addWater")} onClick={onAdd} whileTap={{ scale: 0.93 }}
          className="px-3.5 py-1.5 rounded-xl font-display text-[11px] font-medium flex items-center gap-1.5"
          style={{
            background: cups >= maxCups ? "rgba(139,92,246,0.12)" : "hsl(200 70% 45% / 0.15)",
            border: `1px solid ${cups >= maxCups ? "rgba(139,92,246,0.3)" : "hsl(200 70% 55% / 0.40)"}`,
            color: cups >= maxCups ? "rgba(139,92,246,0.7)" : "hsl(200 90% 70%)",
          }}
        >
          <Droplets size={11} />
          {saving ? t("agenda.saving") : cups >= maxCups ? t("nutrition.ux.waterGoalReached") : t("nutrition.addWater")}
        </motion.button>
      </div>
      <div className="grid grid-cols-8 gap-1.5" aria-hidden="true">
        {Array.from({ length: Math.min(maxCups, 32) }, (_, i) => (
          <motion.div key={i}
            className="flex-1 h-10 rounded-lg flex items-center justify-center relative overflow-hidden"
            initial={false}
            animate={{
              background: i < cups ? "hsl(200 70% 50% / 0.12)" : "rgba(255,255,255,0.02)",
              borderColor: i < cups ? "hsl(200 70% 50% / 0.35)" : "rgba(255,255,255,0.06)",
            }}
            style={{ border: "1px solid" }}
            transition={{ duration: 0.4, delay: i * 0.05 }}
          >
            {i < cups && (
              <motion.div className="absolute bottom-0 left-0 right-0"
                initial={{ height: 0 }} animate={{ height: "100%" }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: "easeOut" }}
                style={{ background: "linear-gradient(to top, hsl(200 70% 50% / 0.18), hsl(200 70% 50% / 0.04))", borderRadius: "inherit" }}
              />
            )}
            <Droplets size={12} className="relative z-10 transition-colors duration-300"
              style={{
                color: i < cups ? "hsl(200 90% 60%)" : "hsl(200 30% 35% / 0.35)",
                filter: i < cups ? "drop-shadow(0 0 6px hsl(200 90% 60% / 0.8))" : undefined,
                animation: i < cups ? `floatUp 2s ease-in-out ${i * 0.15}s infinite` : undefined,
              }} />
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

// ── Meal Card ───────────────────────────────────────────────────────────

const MEAL_ICONS: Record<string, React.ReactNode> = {
  breakfast: <Zap size={13} />, lunch: <Utensils size={13} />,
  snack: <Apple size={13} />, dinner: <Flame size={13} />,
};

const MEAL_ACCENT: Record<string, string> = {
  breakfast: "hsl(38 90% 50% / 0.6)", lunch: "hsl(142 70% 45% / 0.6)",
  snack: "hsl(38 90% 50% / 0.6)", dinner: "hsl(270 70% 60% / 0.6)",
  pre_workout: "hsl(187 72% 50% / 0.6)", post_workout: "hsl(142 70% 45% / 0.6)",
};

function replaceOpacity(color: string, newOpacity: string): string {
  return color.replace(/[\d.]+\)$/, newOpacity + ")");
}

const MealCard: React.FC<{
  meal: { id: string; meal_type: MealType; description: string; kcal: number; protein: number; carbs: number; fat: number; source?: string; confidence?: number | null };
  index: number; t: (k: string) => string;
  onEdit: () => void; onDelete: (id: string) => void;
}> = ({ meal, index, t, onEdit, onDelete }) => {
  const accent = MEAL_ACCENT[meal.meal_type] || "rgba(0,229,255,0.5)";
  const [showActions, setShowActions] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Confidence badge for photo-logged meals
  const photoBadge = (() => {
    if (meal.source !== "photo" || typeof meal.confidence !== "number") return null;
    const c = meal.confidence;
    const color = c >= 0.85 ? "hsl(140 60% 50%)" : c >= 0.60 ? "hsl(38 92% 55%)" : "hsl(0 72% 60%)";
    return { color, label: `${Math.round(c * 100)}%` };
  })();

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.1 + index * 0.08, duration: 0.5 }}
      className="rounded-2xl p-4 relative overflow-hidden"
      style={{
        background: "linear-gradient(135deg, rgba(26,26,46,0.8) 0%, rgba(15,15,30,0.9) 100%)",
        border: `1px solid ${replaceOpacity(accent, "0.18")}`, backdropFilter: "blur(16px)",
      }}
    >
      {/* Photo-source confidence badge (top-right) */}
      {photoBadge && (
        <div
          className="absolute top-2 right-2 z-10 flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-display font-semibold tabular-nums"
          style={{
            background: `${photoBadge.color}22`,
            color: photoBadge.color,
            border: `1px solid ${photoBadge.color}55`,
          }}
          aria-label={`photo confidence ${photoBadge.label}`}
        >
          <Camera size={9} />
          {photoBadge.label}
        </div>
      )}
      <div className="absolute top-0 left-4 right-4 h-[1px]"
        style={{ background: `linear-gradient(90deg, transparent, ${replaceOpacity(accent, "0.25")}, transparent)` }} />
      <div className="flex items-start justify-between relative z-10">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <motion.div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
            style={{
              background: replaceOpacity(accent, "0.1"),
              border: `1px solid ${replaceOpacity(accent, "0.2")}`,
              color: replaceOpacity(accent, "1"),
            }}
          >
            {MEAL_ICONS[meal.meal_type] || <Utensils size={13} />}
          </motion.div>
          <div className="flex-1 min-w-0">
            <p className="text-[9px] font-display tracking-[0.15em] uppercase mb-0.5" style={{ color: accent }}>
              {t(`nutrition.${meal.meal_type}`)}
            </p>
            <p className="font-display text-[14px] font-semibold text-white truncate leading-tight">{meal.description}</p>
            <p className="text-[11px] font-body mt-1 flex items-center gap-1" style={{ color: "hsl(187 72% 60%)", textShadow: "0 0 8px hsl(187 72% 50% / 0.3)" }}>
              <Flame size={10} /> {meal.kcal} kcal
            </p>
          </div>
        </div>
        <div className="flex items-start gap-1 shrink-0 ml-2">
          <div className="flex gap-1 mt-1">
            {[
              { l: "P", v: meal.protein, c: "#a855f6" },
              { l: "C", v: meal.carbs, c: "#3b82f6" },
              { l: "F", v: meal.fat, c: "#eab308" },
            ].map((b) => (
              <span key={b.l} className="text-[11px] font-display font-bold px-2 py-0.5 rounded-md"
                style={{ background: `${b.c}15`, color: b.c, border: `1px solid ${b.c}30` }}>
                {b.l}{b.v}
              </span>
            ))}
          </div>
          <button
            onClick={() => { setShowActions(!showActions); setConfirmDelete(false); }}
            className="w-7 h-7 flex items-center justify-center rounded-lg mt-0.5"
            style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
          >
            <MoreVertical size={14} style={{ color: "rgba(255,255,255,0.30)" }} />
          </button>
        </div>
      </div>

      {/* Action row */}
      <AnimatePresence>
        {showActions && (
          <motion.div
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="relative z-10 pt-2.5 mt-2.5 flex items-center gap-4"
            style={{ borderTop: "1px solid hsl(220 20% 15%)" }}
          >
            {!confirmDelete ? (
              <>
                <button onClick={onEdit}
                  className="flex items-center gap-1.5 text-[12px] font-display font-medium py-1"
                  style={{ color: "hsl(187 72% 55%)", touchAction: "manipulation" }}>
                  <Pencil size={12} /> {t("nutrition.edit_title")}
                </button>
                <button onClick={() => setConfirmDelete(true)}
                  className="flex items-center gap-1.5 text-[12px] font-display font-medium py-1"
                  style={{ color: "hsl(0 72% 60%)", touchAction: "manipulation" }}>
                  <Trash2 size={12} /> {t("nutrition.delete_confirm")}
                </button>
              </>
            ) : (
              <>
                <p className="text-[11px] font-body" style={{ color: "rgba(255,255,255,0.50)" }}>
                  {t("nutrition.delete_confirm")}
                </p>
                <button onClick={() => { onDelete(meal.id); setShowActions(false); }}
                  className="text-[12px] font-display font-semibold px-2.5 py-1 rounded-lg"
                  style={{ background: "hsl(0 72% 50% / 0.15)", color: "hsl(0 72% 60%)", border: "1px solid hsl(0 72% 50% / 0.30)", touchAction: "manipulation" }}>
                  {t("nutrition.confirm_yes")}
                </button>
                <button onClick={() => setConfirmDelete(false)}
                  className="text-[12px] font-display font-medium py-1"
                  style={{ color: "rgba(255,255,255,0.40)", touchAction: "manipulation" }}>
                  {t("nutrition.confirm_no")}
                </button>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ── Weekly Overview ─────────────────────────────────────────────────────

function dayLabel(dateStr: string, t: (k: string) => string): string {
  const d = new Date(dateStr + "T12:00:00");
  const dayKeys = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
  return t(`common.days.${dayKeys[d.getDay()]}`);
}

const WeeklyOverview: React.FC<{
  weekData: { date: string; pct: number }[]; t: (k: string) => string;
}> = ({ weekData, t }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
    className="rounded-2xl p-4 relative overflow-hidden"
    style={{
      background: "linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.65)), url(https://lluvykhqgdnbfobjixbj.supabase.co/storage/v1/object/public/mirror-brief/room%20ex_012.jpeg) center/cover no-repeat",
      border: "1px solid hsl(187 72% 50% / 0.22)",
    }}
  >
    <div className="absolute top-0 left-0 right-0 h-[1px]"
      style={{ background: "linear-gradient(90deg, transparent, rgba(0,229,255,0.15), transparent)" }} />
    <div className="flex items-center gap-2 mb-4">
      <TrendingUp size={13} style={{ color: "hsl(187 72% 50% / 0.65)" }} />
      <p className="text-[10px] font-display tracking-[0.15em] uppercase" style={{ color: "hsl(187 72% 50% / 0.65)" }}>
        {t("nutrition.thisWeek")}
      </p>
    </div>
    <div className="flex gap-2 items-end mb-3 relative" style={{ height: 80 }}>
      {weekData.every(d => d.pct === 0) && (
        <p className="absolute inset-0 flex items-center justify-center text-[11px] font-body italic z-10 pointer-events-none"
          style={{ color: "rgba(255,255,255,0.30)" }}>
          {t("nutrition.weekly_empty")}
        </p>
      )}
      {weekData.map((d, i) => {
        const allZero = weekData.every(w => w.pct === 0);
        const barH = Math.max(d.pct * 0.7, 4);
        const green = d.pct >= 80;
        const yellow = d.pct >= 50 && d.pct < 80;
        const barColor = green ? "hsl(142 70% 45%)" : yellow ? "hsl(45 90% 55%)" : "hsl(0 72% 55%)";
        const glowC = green ? "rgba(34,197,94,0.25)" : yellow ? "rgba(234,179,8,0.2)" : "rgba(239,68,68,0.15)";
        return (
          <div key={i} className="flex-1 flex flex-col items-center gap-1.5 relative z-0">
            <p className="text-[8px] font-display font-bold" style={{ color: barColor, opacity: allZero ? 0 : 0.85 }}>{d.pct}%</p>
            <motion.div className="w-full rounded-lg relative overflow-hidden"
              initial={{ height: 0 }} animate={{ height: barH }}
              transition={{ delay: 0.3 + i * 0.08, duration: 0.6, ease: "easeOut" }}
              style={{
                background: `linear-gradient(to top, ${barColor}, ${replaceOpacity(barColor, "0.25")})`,
                boxShadow: `0 0 12px ${glowC}`,
                border: `1px solid ${replaceOpacity(barColor, "0.3")}`,
              }}
            />
            <p className="text-[9px] font-display" style={{ color: "rgba(255,255,255,0.45)" }}>{dayLabel(d.date, t)}</p>
          </div>
        );
      })}
    </div>
    <div className="flex gap-4 justify-center">
      {[
        { l: "\u226580%", c: "rgba(34,197,94,0.7)" },
        { l: "50\u201379%", c: "rgba(234,179,8,0.6)" },
        { l: "<50%", c: "rgba(239,68,68,0.5)" },
      ].map((item) => (
        <div key={item.l} className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full" style={{ background: item.c }} />
          <span className="text-[8px] font-body" style={{ color: "rgba(255,255,255,0.3)" }}>{item.l}</span>
        </div>
      ))}
    </div>
  </motion.div>
);

// ── Mirror Content Slot ─────────────────────────────────────────────────

const MirrorInsightSlot: React.FC<{
  titleKey: string; fallbackKey: string; t: (k: string) => string;
  navigate: (path: string) => void; ctaKey: string;
  dynamicContent?: string | null; isLoadingAI?: boolean;
  // Alvo de denuncia. Opcional porque o segundo slot desta pagina nao recebe
  // dynamicContent (nao renderiza nada hoje) — so o slot com texto de modelo
  // ganha o controle.
  reportContentId?: string | null;
}> = ({ titleKey, fallbackKey, t, navigate, ctaKey, dynamicContent, isLoadingAI, reportContentId }) => {
  if (!isLoadingAI && (!dynamicContent || dynamicContent.trim() === "")) {
    return null;
  }
  return (
  <motion.div
    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
    className="rounded-2xl p-5 relative overflow-hidden"
    style={{
      background: "linear-gradient(135deg, rgba(0,0,0,0.30) 0%, rgba(10,8,20,0.45) 100%), url(https://lluvykhqgdnbfobjixbj.supabase.co/storage/v1/object/public/mirror-brief/room%20ex_01.jpeg) center/cover no-repeat",
      border: "1.5px solid hsl(187 72% 50% / 0.55)",
      boxShadow: "0 0 30px rgba(0,229,255,0.06), inset 0 1px 0 hsl(187 72% 50% / 0.15)",
      backdropFilter: "blur(2px)",
    }}
  >
    <div className="absolute top-0 left-0 right-0 h-[1px]"
      style={{ background: "linear-gradient(90deg, transparent 10%, rgba(0,229,255,0.35) 50%, transparent 90%)" }} />
    <div className="absolute top-0 right-0 w-24 h-24 rounded-full opacity-20"
      style={{ background: "radial-gradient(circle, rgba(139,92,246,0.3), transparent 70%)" }} />
    <div className="flex items-start gap-3 relative z-10">
      <motion.div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
        style={{ background: "rgba(0,229,255,0.08)", border: "1px solid rgba(0,229,255,0.18)" }}
        animate={{ boxShadow: ["0 0 0px rgba(0,229,255,0)", "0 0 16px rgba(0,229,255,0.2)", "0 0 0px rgba(0,229,255,0)"] }}
        transition={{ duration: 4, repeat: Infinity }}
      >
        <Star size={14} style={{ color: "#00e5ff" }} />
      </motion.div>
      <div className="flex-1 min-w-0">
        <p className="text-[9px] font-display tracking-[0.2em] uppercase mb-2" style={{ color: "rgba(0,229,255,0.80)" }}>
          {t(titleKey)}
        </p>
        {isLoadingAI ? (
          <div className="flex items-center gap-2 py-2">
            <Loader2 size={12} className="animate-spin" style={{ color: "rgba(0,229,255,0.5)" }} />
            <p className="text-[11px] font-body italic" style={{ color: "rgba(255,255,255,0.5)" }}>{t("mirror.thinking")}</p>
          </div>
        ) : (
          <div className="flex items-start justify-between gap-2">
            <p className="text-[12px] font-body leading-relaxed italic flex-1" style={{ color: "rgba(255,255,255,0.88)" }}>
              {dynamicContent || t(fallbackKey)}
            </p>
            {/* Denuncia da reacao a refeicao registrada. So quando o texto veio
                do modelo (dynamicContent) — o fallback e chave de i18n e nao
                se denuncia copy propria. */}
            {reportContentId && dynamicContent && (
              <ReportContentButton
                surface={REPORT_SURFACES.REACTION_NUTRITION}
                contentId={reportContentId}
                snapshot={dynamicContent}
                context={{ module: "nutrition" }}
              />
            )}
          </div>
        )}
        <div className="flex items-center gap-4 mt-3 flex-wrap">
          <motion.button onClick={() => navigate("/mirror")}
            className="flex items-center gap-1.5 font-display text-[11px] font-medium tracking-wide"
            style={{ color: "hsl(187 72% 65%)" }} whileHover={{ x: 3 }} whileTap={{ scale: 0.96 }}
          >
            <span>{t(ctaKey)}</span>
            <ArrowRight size={11} />
          </motion.button>
          {dynamicContent && typeof window !== "undefined" && "speechSynthesis" in window && (
            <motion.button
              onClick={() => {
                try {
                  window.speechSynthesis.cancel();
                  const utterance = new SpeechSynthesisUtterance(dynamicContent);
                  const voices = window.speechSynthesis.getVoices();
                  const ptBRVoice =
                    voices.find(v => v.lang === "pt-BR" || v.lang === "pt_BR") ||
                    voices.find(v => v.lang.toLowerCase().replace("_", "-") === "pt-br") ||
                    voices.find(v => v.lang.toLowerCase().startsWith("pt-br")) ||
                    voices.find(v => v.lang.toLowerCase().startsWith("pt") && !v.lang.toLowerCase().includes("pt-pt") && !v.lang.toLowerCase().includes("pt_pt")) ||
                    voices.find(v => v.lang.toLowerCase().startsWith("pt"));
                  if (ptBRVoice) utterance.voice = ptBRVoice;
                  utterance.rate = 1.0;
                  utterance.pitch = 1.0;
                  window.speechSynthesis.speak(utterance);
                } catch (err) {
                  console.warn("[TTS] speak failed", err);
                }
              }}
              whileTap={{ scale: 0.92 }}
              className="flex items-center gap-1.5 font-display text-[10px] font-medium tracking-wide"
              style={{ color: "rgba(0,229,255,0.55)" }}
              aria-label={t("common.listen")}
            >
              <Volume2 size={11} />
              <span>{t("common.listen")}</span>
            </motion.button>
          )}
        </div>
      </div>
    </div>
  </motion.div>
  );
};

// ── Quick Log Form ──────────────────────────────────────────────────────

const ALL_MEAL_TYPES: { key: MealType; emoji: string; labelKey: string }[] = [
  { key: "breakfast", emoji: "🌅", labelKey: "nutrition.breakfast" },
  { key: "lunch", emoji: "☀️", labelKey: "nutrition.lunch" },
  { key: "dinner", emoji: "🌙", labelKey: "nutrition.dinner" },
  { key: "snack", emoji: "🍎", labelKey: "nutrition.snack" },
  { key: "pre_workout", emoji: "⚡", labelKey: "nutrition.pre_workout" },
  { key: "post_workout", emoji: "💪", labelKey: "nutrition.post_workout" },
];

const NAME_HINTS = ["Frango grelhado", "Whey protein", "Aveia com frutas", "Omelete", "Salada Caesar"];

export const QuickLogForm: React.FC<{
  onSubmit: (data: { client_action_id: string; meal_type: MealType; description: string; kcal: number; protein: number; carbs: number; fat: number }) => Promise<void>;
  onSavingChange: (saving: boolean) => void;
  onClose: () => void; t: (k: string) => string; autoStartVoice?: boolean;
  onOpenFoodSearch: (initialQuery?: string) => void;
  selectedFood: { name: string; kcal: string; protein: string; carbs: string; fat: string } | null;
  recentDescriptions: string[];
}> = ({ onSubmit, onSavingChange, onClose, t, autoStartVoice, onOpenFoodSearch, selectedFood, recentDescriptions }) => {
  const { lang } = useLanguage();
  const voice = useVoiceInput(lang);
  const [mealType, setMealType] = useState<MealType>("lunch");
  const [desc, setDesc] = useState("");
  const [kcal, setKcal] = useState("");
  const [protein, setProtein] = useState("");
  const [carbs, setCarbs] = useState("");
  const [fat, setFat] = useState("");
  const [notes, setNotes] = useState("");
  const [showNotes, setShowNotes] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  // One ID belongs to this opened draft, including retries after a lost response.
  const [clientActionId] = useState(nutritionActionId);
  const saveLock = useRef(false);
  const [saveError, setSaveError] = useState(false);
  const autoStarted = useRef(false);
  const kcalInputRef = useRef<HTMLInputElement>(null);
  // One recognizer serves both dictated fields (two would fight over the mic),
  // so it has to remember which one is receiving and what it already held.
  const voiceTarget = useRef<"desc" | "notes">("desc");
  const voiceBaseline = useRef("");

  const applyVoice = (text: string) => {
    const merged = voiceBaseline.current ? `${voiceBaseline.current} ${text}` : text;
    if (voiceTarget.current === "notes") setNotes(merged);
    else setDesc(merged);
  };

  const startVoiceFor = (target: "desc" | "notes", current: string) => {
    voiceTarget.current = target;
    voiceBaseline.current = current.trim();
    voice.startListening();
  };

  // startListening() takes no callback; both call sites here used to pass one,
  // so nothing dictated ever reached desc or notes (P0 28/07).
  useEffect(() => {
    if (!voice.transcript) return;
    applyVoice(voice.transcript);
  }, [voice.transcript]);

  // Apply selected food from FoodSearchScreen
  useEffect(() => {
    if (selectedFood) {
      setDesc(selectedFood.name);
      setKcal(selectedFood.kcal);
      setProtein(selectedFood.protein);
      setCarbs(selectedFood.carbs);
      setFat(selectedFood.fat);
      setTimeout(() => kcalInputRef.current?.focus(), 50);
    }
  }, [selectedFood]);

  // Auto-start voice for name field
  useEffect(() => {
    if (autoStartVoice && voice.supported && !autoStarted.current) {
      autoStarted.current = true;
      startVoiceFor("desc", desc);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStartVoice, voice.supported]);

  const handleChipTap = (hint: string) => {
    onOpenFoodSearch(hint);
  };

  // Validation
  const errors: Record<string, string | null> = {
    desc: !desc.trim() ? t("nutrition.descRequired") : desc.trim().length < 2 ? t("nutrition.descRequired") : null,
    kcal: !kcal || !Number.isFinite(Number(kcal)) || Number(kcal) < 0 ? t("nutrition.kcalLabel") + " ≥ 0" : null,
    protein: protein !== "" && (!Number.isFinite(Number(protein)) || Number(protein) < 0) ? ">= 0" : null,
    carbs: carbs !== "" && (!Number.isFinite(Number(carbs)) || Number(carbs) < 0) ? ">= 0" : null,
    fat: fat !== "" && (!Number.isFinite(Number(fat)) || Number(fat) < 0) ? ">= 0" : null,
  };
  const isValid = !errors.desc && !errors.kcal && !errors.protein && !errors.carbs && !errors.fat;
  const touch = (field: string) => setTouched(prev => ({ ...prev, [field]: true }));

  const toggleNotesVoice = () => {
    if (voice.isListening) {
      const finalText = voice.stopListening();
      if (finalText) applyVoice(finalText);
      return;
    }
    startVoiceFor("notes", notes);
  };

  const handleSubmit = async () => {
    setTouched({ desc: true, kcal: true, protein: true, carbs: true, fat: true });
    if (!isValid || saveLock.current) return;
    saveLock.current = true; setSaveError(false); onSavingChange(true); setSubmitting(true);
    try {
      await onSubmit({
        client_action_id: clientActionId,
        meal_type: mealType, description: [desc.trim(), notes.trim()].filter(Boolean).join("\n"),
        kcal: parseInt(kcal) || 0, protein: parseInt(protein) || 0,
        carbs: parseInt(carbs) || 0, fat: parseInt(fat) || 0,
      });
      onClose();
    } catch { setSaveError(true); } finally {
      saveLock.current = false; onSavingChange(false); setSubmitting(false);
    }
  };

  const inputStyle = {
    background: "hsl(220 20% 7%)",
    border: "1px solid hsl(220 20% 18%)",
    transition: "border-color 0.2s, box-shadow 0.2s",
  };
  const inputFocusClass = "focus:border-[hsl(187_72%_50%_/_0.50)] focus:outline-none focus:shadow-[0_0_0_2px_hsl(187_72%_50%_/_0.15)]";

  return (
    <>
      {saveError && <p role="alert" className="px-6 text-sm text-destructive">{t("nutrition.ux.saveRetry")}</p>}
      <fieldset disabled={submitting} className="contents">
      <div className="space-y-5 overflow-y-auto flex-1 px-6 pt-4 pb-2"
        style={{ WebkitOverflowScrolling: "touch" as any, overscrollBehavior: "contain", touchAction: "pan-y" }}>
        {/* Meal type selector — 6 pills */}
        <div>
          <p className="text-[10px] font-display tracking-[0.15em] uppercase mb-2.5"
            style={{ color: "rgba(255,255,255,0.35)" }}>{t("nutrition.mealType")}</p>
          <div className="grid grid-cols-3 gap-2">
            {ALL_MEAL_TYPES.map(({ key: mt, emoji, labelKey }) => {
              const active = mealType === mt;
              return (
                <motion.button key={mt} aria-pressed={active} onClick={() => setMealType(mt)} whileTap={{ scale: 0.94 }}
                  className="py-2.5 rounded-xl font-display text-[10px] font-medium flex flex-col items-center gap-1 cursor-pointer"
                  style={{
                    background: active ? "hsl(187 72% 50% / 0.15)" : "hsl(220 20% 8% / 0.60)",
                    border: `1px solid ${active ? "hsl(187 72% 50% / 0.60)" : "hsl(220 20% 20%)"}`,
                    color: active ? "hsl(187 72% 70%)" : "rgba(255,255,255,0.50)",
                    boxShadow: active ? "0 0 12px rgba(0,229,255,0.1)" : "none",
                    transform: active ? "scale(1.02)" : "scale(1)",
                    touchAction: "manipulation",
                    WebkitTapHighlightColor: "transparent",
                  }}
                >
                  <span className="text-xl leading-none">{emoji}</span>
                  {t(labelKey)}
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Food selection — tappable field */}
        <div>
          <p className="text-[10px] font-display tracking-[0.15em] uppercase mb-1.5"
            style={{ color: "rgba(255,255,255,0.35)" }}>{t("nutrition.description")}</p>
          <textarea aria-label={t("nutrition.description")} value={desc} onChange={event => setDesc(event.target.value)} rows={2}
            placeholder={t("nutrition.descPlaceholder")} className="w-full rounded-xl px-3 py-3 text-sm text-white bg-background border border-border focus-visible:ring-2 focus-visible:ring-primary" />
          <button type="button" onClick={() => onOpenFoodSearch(desc)} className="min-h-11 text-sm text-primary underline">{t("nutrition.search_select")}</button>
          {touched.desc && errors.desc && (
            <p className="text-[10px] mt-1" style={{ color: "rgba(244,63,94,0.7)" }}>{errors.desc}</p>
          )}
          {/* Hint chips */}
          <div className="flex gap-1.5 mt-2 flex-wrap">
            {(() => {
              const chipSource = recentDescriptions.length >= 3 ? recentDescriptions : NAME_HINTS;
              return chipSource.slice(0, 3).map(hint => (
                <button key={hint} onClick={() => handleChipTap(hint)}
                  className="text-[10px] rounded-full px-3 py-1 font-body"
                  style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.10)", color: "rgba(255,255,255,0.45)" }}>
                  {hint}
                </button>
              ));
            })()}
          </div>
        </div>

        {/* Calories with inline unit */}
        <div>
          <div className="flex items-center gap-1 mb-1">
            <Flame size={10} style={{ color: "#00e5ff", opacity: 0.6 }} />
            <p className="text-[9px] font-display tracking-wide" style={{ color: "rgba(255,255,255,0.3)" }}>{t("nutrition.kcalLabel")}</p>
          </div>
          <div className="relative">
            <input aria-label={t("nutrition.kcalLabel")} ref={kcalInputRef} value={kcal} onChange={(e) => setKcal(e.target.value.replace(/\D/g, ""))}
              onBlur={() => touch("kcal")} placeholder="0" inputMode="numeric"
              className={`w-full px-3 py-2.5 pr-12 rounded-xl text-[13px] font-body text-white placeholder:text-white/15 ${inputFocusClass}`}
              style={{ ...inputStyle, borderColor: touched.kcal && errors.kcal ? "rgba(244,63,94,0.4)" : undefined }} />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-display" style={{ color: "rgba(255,255,255,0.25)" }}>kcal</span>
          </div>
          {touched.kcal && errors.kcal && (
            <p className="text-[10px] mt-0.5" style={{ color: "rgba(244,63,94,0.7)" }}>{errors.kcal}</p>
          )}
        </div>

        {/* Macros row — 3 compact inputs */}
        <div>
          <p className="text-[10px] font-display tracking-[0.15em] uppercase mb-2"
            style={{ color: "rgba(255,255,255,0.35)" }}>Macros</p>
          <div className="grid grid-cols-3 gap-2">
            {[
              { key: "protein", val: protein, set: setProtein, label: t("nutrition.protein"), icon: <Zap size={10} />, c: "#a855f6" },
              { key: "carbs", val: carbs, set: setCarbs, label: t("nutrition.carbs"), icon: <TrendingUp size={10} />, c: "#3b82f6" },
              { key: "fat", val: fat, set: setFat, label: t("nutrition.fat"), icon: <Droplets size={10} />, c: "#eab308" },
            ].map((f) => (
              <div key={f.key}>
                <div className="flex items-center gap-1 mb-1">
                  <span style={{ color: f.c, opacity: 0.6 }}>{f.icon}</span>
                  <p className="text-[9px] font-display tracking-wide" style={{ color: "rgba(255,255,255,0.3)" }}>{f.label}</p>
                </div>
                <div className="relative">
                  <input aria-label={f.label} value={f.val} onChange={(e) => f.set(e.target.value.replace(/\D/g, ""))}
                    onBlur={() => touch(f.key)} placeholder="0" inputMode="numeric"
                    className={`w-full px-2 py-2.5 pr-6 rounded-xl text-[13px] font-body text-white text-center placeholder:text-white/15 ${inputFocusClass}`}
                    style={{ ...inputStyle, boxShadow: f.val ? `0 0 8px ${f.c}10` : "none", borderColor: touched[f.key] && errors[f.key] ? "rgba(244,63,94,0.4)" : undefined }} />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] font-display" style={{ color: "rgba(255,255,255,0.20)" }}>g</span>
                </div>
                {touched[f.key] && errors[f.key] && (
                  <p className="text-[10px] mt-0.5 text-center" style={{ color: "rgba(244,63,94,0.7)" }}>{errors[f.key]}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Notes — collapsible */}
        <div>
          {!showNotes ? (
            <button onClick={() => setShowNotes(true)}
              className="text-[11px] font-display font-medium flex items-center gap-1.5"
              style={{ color: "rgba(0,229,255,0.5)" }}>
              <Plus size={11} /> {t("nutrition.addNote")}
            </button>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-[10px] font-display tracking-[0.15em] uppercase"
                  style={{ color: "rgba(255,255,255,0.35)" }}>{t("nutrition.notes")}</p>
                {voice.supported && (
                  <motion.button onClick={toggleNotesVoice} whileTap={{ scale: 0.88 }}
                    className="w-6 h-6 rounded-md flex items-center justify-center"
                    style={{ background: voice.isListening ? "rgba(0,229,255,0.15)" : "rgba(0,229,255,0.08)" }}>
                    {voice.isListening ? <MicOff size={10} style={{ color: "#00e5ff" }} /> : <Mic size={10} style={{ color: "rgba(0,229,255,0.4)" }} />}
                  </motion.button>
                )}
              </div>
              <textarea aria-label={t("nutrition.notes")} value={notes} onChange={(e) => setNotes(e.target.value)}
                rows={2} placeholder={t("nutrition.notesPlaceholder")}
                className={`w-full px-3 py-2.5 rounded-xl text-[12px] font-body text-white placeholder:text-white/20 resize-none ${inputFocusClass}`}
                style={inputStyle} />
            </div>
          )}
        </div>
      </div>

      {/* Fixed save button */}
      <div className="shrink-0 px-6 pb-6 pt-3"
        style={{ background: "linear-gradient(transparent, hsl(var(--card)) 30%)" }}>
        <motion.button
          type="button"
          onClick={handleSubmit}
          whileTap={!submitting ? { scale: 0.96 } : undefined}
          disabled={submitting}
          className="w-full py-3.5 rounded-2xl font-display text-[14px] font-semibold tracking-wide relative overflow-hidden cursor-pointer"
          style={{
            background: "linear-gradient(135deg, hsl(187 72% 45%), hsl(200 72% 50%))",
            color: "#0a0a0f",
            boxShadow: submitting || !isValid ? "none" : "0 4px 20px hsl(187 72% 45% / 0.40)",
            opacity: submitting || !isValid ? 0.4 : 1,
            WebkitTapHighlightColor: "transparent",
            touchAction: "manipulation",
          }}
        >

          <span className="relative z-10 flex items-center justify-center gap-2">
            {submitting ? <Loader2 size={14} className="animate-spin" /> : t("nutrition.saveMeal")}
          </span>
        </motion.button>
      </div>
      </fieldset>
    </>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════

// ── Edit Meal Modal ────────────────────────────────────────────────────

export const EditMealModal: React.FC<{
  meal: { id: string; meal_type: MealType; description: string; kcal: number; protein: number; carbs: number; fat: number };
  onSave: (id: string, data: { description: string; kcal: number; protein: number; carbs: number; fat: number; meal_type: string }) => Promise<void>;
  onClose: () => void;
  t: (k: string) => string;
  onOpenFoodSearch: (initialQuery?: string) => void;
  selectedFood: { name: string; kcal: string; protein: string; carbs: string; fat: string } | null;
}> = ({ meal, onSave, onClose, t, onOpenFoodSearch, selectedFood }) => {
  const [mealType, setMealType] = useState<MealType>(meal.meal_type);
  const [desc, setDesc] = useState(meal.description);
  const [kcal, setKcal] = useState(meal.kcal.toString());
  const [protein, setProtein] = useState(meal.protein.toString());
  const [carbs, setCarbs] = useState(meal.carbs.toString());
  const [fat, setFat] = useState(meal.fat.toString());
  const [submitting, setSubmitting] = useState(false);

  const saveLock = useRef(false);
  const [saveError, setSaveError] = useState(false);
  const close = () => { if (!saveLock.current) onClose(); };
  useEffect(() => {
    if (selectedFood) {
      setDesc(selectedFood.name);
      setKcal(selectedFood.kcal);
      setProtein(selectedFood.protein);
      setCarbs(selectedFood.carbs);
      setFat(selectedFood.fat);
    }
  }, [selectedFood]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    };
  }, []);

  const handleSave = async () => {
    if (saveLock.current || !desc.trim() || !kcal || [kcal,protein,carbs,fat].some(value => !Number.isFinite(Number(value)) || Number(value) < 0)) return;
    saveLock.current = true; setSaveError(false); setSubmitting(true);
    try {
      await onSave(meal.id, {
        description: desc.trim(),
        kcal: parseInt(kcal) || 0,
        protein: parseInt(protein) || 0,
        carbs: parseInt(carbs) || 0,
        fat: parseInt(fat) || 0,
        meal_type: mealType,
      });
    } catch { setSaveError(true); } finally {
      saveLock.current = false; setSubmitting(false);
    }
  };

  const inputStyle = { background: "hsl(220 20% 7%)", border: "1px solid hsl(220 20% 18%)" };
  const inputCls = "w-full px-3 py-2.5 rounded-xl text-[13px] font-body text-white placeholder:text-white/15 focus:outline-none focus:border-[hsl(187_72%_50%_/_0.50)]";

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] flex items-end justify-center"
      style={{ background: "rgba(5,5,12,0.82)", backdropFilter: "blur(8px)" }}
      onClick={close}>
      <motion.div
        initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
        transition={{ type: "spring", damping: 28, stiffness: 300 }}
        className="w-full max-w-md rounded-t-3xl flex flex-col"
        style={{ background: "hsl(220 25% 6%)", border: "1px solid rgba(0,229,255,0.15)", borderBottom: "none", maxHeight: "92dvh", boxShadow: "inset 0 1px 0 hsl(187 72% 50% / 0.20)" }}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-12 h-1 rounded-full" style={{ background: "hsl(187 72% 50% / 0.30)" }} />
        </div>
        <div className="flex items-center justify-between px-6 pt-3 pb-2 shrink-0">
          <p className="font-display text-lg font-semibold text-white">{t("nutrition.edit_title")}</p>{saveError && <p role="alert">{t("nutrition.ux.saveRetry")}</p>}
          <motion.button disabled={submitting} aria-label={t("common.close")} onClick={close} whileTap={{ scale: 0.85 }}
            className="w-8 h-8 rounded-xl flex items-center justify-center"
            style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
            <X size={16} style={{ color: "rgba(255,255,255,0.4)" }} />
          </motion.button>
        </div>

        <fieldset disabled={submitting} className="contents"><div className="flex-1 overflow-y-auto px-6 pt-3 pb-4 space-y-4" style={{ WebkitOverflowScrolling: "touch" as any, overscrollBehavior: "contain" }}>
          {/* Meal type pills */}
          <div className="grid grid-cols-3 gap-2">
            {ALL_MEAL_TYPES.map(({ key: mt, emoji, labelKey }) => {
              const active = mealType === mt;
              return (
                <button key={mt} aria-pressed={active} onClick={() => setMealType(mt)}
                  className="py-2 rounded-xl font-display text-[10px] font-medium flex flex-col items-center gap-1"
                  style={{
                    background: active ? "hsl(187 72% 50% / 0.15)" : "hsl(220 20% 8% / 0.60)",
                    border: `1px solid ${active ? "hsl(187 72% 50% / 0.60)" : "hsl(220 20% 20%)"}`,
                    color: active ? "hsl(187 72% 70%)" : "rgba(255,255,255,0.50)",
                    touchAction: "manipulation",
                  }}>
                  <span className="text-xl leading-none">{emoji}</span>
                  {t(labelKey)}
                </button>
              );
            })}
          </div>

          {/* Description */}
          <div>
            <p className="text-[10px] font-display tracking-[0.15em] uppercase mb-1.5" style={{ color: "rgba(255,255,255,0.35)" }}>{t("nutrition.description")}</p>
            <textarea aria-label={t("nutrition.description")} value={desc} onChange={event => setDesc(event.target.value)} rows={2}
              className="w-full rounded-xl px-3 py-3 text-sm text-white bg-background border border-border focus-visible:ring-2 focus-visible:ring-primary" />
            <button type="button" onClick={() => onOpenFoodSearch(desc)} className="min-h-11 text-sm text-primary underline">{t("nutrition.search_select")}</button>
          </div>

          {/* Kcal */}
          <div>
            <p className="text-[9px] font-display tracking-wide mb-1" style={{ color: "rgba(255,255,255,0.3)" }}>{t("nutrition.kcalLabel")}</p>
            <input aria-label={t("nutrition.kcalLabel")} value={kcal} onChange={(e) => setKcal(e.target.value.replace(/\D/g, ""))}
              placeholder="0" inputMode="numeric" className={inputCls} style={inputStyle} />
          </div>

          {/* Macros */}
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: t("nutrition.protein"), val: protein, set: setProtein },
              { label: t("nutrition.carbs"), val: carbs, set: setCarbs },
              { label: t("nutrition.fat"), val: fat, set: setFat },
            ].map((f) => (
              <div key={f.label}>
                <p className="text-[9px] font-display tracking-wide mb-1" style={{ color: "rgba(255,255,255,0.3)" }}>{f.label}</p>
                <input aria-label={f.label} value={f.val} onChange={(e) => f.set(e.target.value.replace(/\D/g, ""))}
                  placeholder="0" inputMode="numeric" className={`${inputCls} text-center`} style={inputStyle} />
              </div>
            ))}
          </div>
        </div>

        {/* Save button */}
        <div className="shrink-0 px-6 pb-6 pt-3">
          <button type="button" onClick={handleSave} disabled={submitting}
            className="w-full py-3.5 rounded-2xl font-display text-[14px] font-semibold tracking-wide cursor-pointer"
            style={{
              background: "linear-gradient(135deg, hsl(187 72% 45%), hsl(200 72% 50%))",
              color: "#0a0a0f",
              boxShadow: submitting ? "none" : "0 4px 20px hsl(187 72% 45% / 0.40)",
              opacity: submitting ? 0.4 : 1,
              touchAction: "manipulation",
            }}>
            {submitting ? <Loader2 size={14} className="animate-spin mx-auto" /> : t("nutrition.save_changes")}
          </button>
        </div>
      </fieldset>
      </motion.div>
    </motion.div>
  );
};

const NutritionContent: React.FC = () => {
  const navigate = useNavigate();
  const { t, tf, lang } = useLanguage();
  const mirrorReact = useNutritionReact();
  const { isReported } = useReportedContent();
  // Identidade da reacao que esta na tela (nutrition_logs.id).
  const [reactedMealId, setReactedMealId] = useState<string | null>(null);

  // Instrumentation (módulo liberado no Free): open-by-plan. Fires once the
  // subscription tier resolves (skips the loading flash). `planLoading` alias
  // avoids shadowing the nutrition hook's own `isLoading` below.
  const { plan, isLoading: planLoading } = usePremium();
  useEffect(() => {
    if (planLoading) return;
    analytics.moduleOpenTier("nutrition", plan);
  }, [planLoading, plan]);
  const {
    todayMeals, todayMacros, cups, weekData, targets,
    isLoading, error, logMeal, deleteLog, updateLog, addWater, reload,
    getLogsHistory, hydration, isSaving, userId, checkedAt, hasCustomGoals,
  } = useNutrition();
  const [captureSaving, setCaptureSaving] = useState(false);
  const captureLock = useRef(false);
  const [modal, setModal] = useState(false);
  const [showPhotoCapture, setShowPhotoCapture] = useState(false);
  const [autoStartVoice, setAutoStartVoice] = useState(false);
  const [showFoodSearch, setShowFoodSearch] = useState(false);
  const [foodSearchInitialQuery, setFoodSearchInitialQuery] = useState("");
  const [selectedFood, setSelectedFood] = useState<{ name: string; kcal: string; protein: string; carbs: string; fat: string } | null>(null);
  const [editingMeal, setEditingMeal] = useState<{ id: string; meal_type: MealType; description: string; kcal: number; protein: number; carbs: number; fat: number } | null>(null);
  const [recentDescriptions, setRecentDescriptions] = useState<string[]>([]);

  const handleOpenFoodSearch = (initialQuery?: string) => {
    setFoodSearchInitialQuery(initialQuery || "");
    setShowFoodSearch(true);
  };

  const handleFoodSelect = (food: LocalFood) => {
    setSelectedFood({
      name: food.name,
      kcal: food.kcal.toString(),
      protein: food.protein.toString(),
      carbs: food.carbs.toString(),
      fat: food.fat.toString(),
    });
    setShowFoodSearch(false);
  };

  // ── Meal history (beyond today) — ModuleHistoryList overlay ──
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLogs, setHistoryLogs] = useState<NutritionLog[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [historyError, setHistoryError] = useState(false);
  const historyRequest = useRef(0);
  const reloadMealHistory = useCallback(async () => {
    if (!userId) return;
    const ticket = ++historyRequest.current;
    setHistoryLoading(true); setHistoryError(false);
    try {
      const rows = await getLogsHistory(365);
      if (ticket === historyRequest.current) setHistoryLogs(rows);
    } catch (error) {
      if (ticket === historyRequest.current) { console.error("[Nutrition] history failed", error); setHistoryError(true); }
    } finally { if (ticket === historyRequest.current) setHistoryLoading(false); }
  }, [getLogsHistory, userId]);
  useEffect(() => { void reloadMealHistory(); return () => { historyRequest.current++; }; }, [reloadMealHistory, checkedAt]);
  useEffect(() => {
    const unique = [...new Set(historyLogs.map(row => row.description.trim()).filter(Boolean))];
    setRecentDescriptions(unique.slice(0, 5));
  }, [historyLogs]);

  // Shared analytics engine — daily macro totals + trend + projection + a
  // meals-logged streak. Descriptive only (no prescription).
  const [nutritionGran, setNutritionGran] = useState<Granularity>(NUTRITION_DASHBOARD.defaultGranularity);
  const nutritionAnalytics = useMemo(
    () => buildDashboard(extractNutritionSamples(historyLogs), NUTRITION_DASHBOARD, nutritionGran),
    [historyLogs, nutritionGran],
  );

  // Body scroll-lock while EITHER full-screen bottom sheet is open (the
  // log-meal modal or the history overlay) — without it, touch scroll chains
  // to the page underneath once the sheet's list hits its end.
  useEffect(() => {
    if (modal || historyOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
      return () => {
        document.body.style.overflow = "";
        document.body.style.position = "";
        document.body.style.width = "";
      };
    }
  }, [modal, historyOpen]);

  const openMealHistory = useCallback(() => {
    setHistoryOpen(true);
    reloadMealHistory();
  }, [reloadMealHistory]);

  const handleDeleteMeal = useCallback(async (id: string) => {
    try {
      await deleteLog(id);
      toast.success(t("nutrition.delete_success"));
    } catch {
      toast.error(t("nutrition.log_error"));
    }
  }, [deleteLog, t]);

  const handleUpdateMeal = useCallback(async (id: string, data: { description: string; kcal: number; protein: number; carbs: number; fat: number; meal_type: string }) => {
    try {
      await updateLog(id, data);
      toast.success(t("nutrition.edit_success"));
      setEditingMeal(null);
      if (historyOpen) reloadMealHistory();
    } catch {
      toast.error(t("nutrition.log_error"));
      throw new Error("Meal update failed");
    }
  }, [updateLog, t, historyOpen, reloadMealHistory]);

  const handleAddWater = useCallback(async () => {
    try { await addWater(); } catch { toast.error(t("nutrition.log_error")); }
  }, [addWater, t]);

  const handleLogMeal = useCallback(async (data: Parameters<typeof logMeal>[0]) => {
    const saved = await logMeal(data);
    toast.success(t("nutrition.log_success"));
    if (saved.xpAwarded) toast(t("nutrition.xp_earned"), { duration: 2000 });
    setReactedMealId(saved.id);
    void mirrorReact.react(saved.id, edgeLocaleFor(lang));
  }, [logMeal, t, lang, mirrorReact]);

  if (error && !checkedAt) return <MobileLayout className="p-6"><MirrorBackButton /><p role="alert">{t("nutrition.error")}</p><button onClick={() => void reload()}>{t("common.retry")}</button></MobileLayout>;

  // ── Loading ─────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <MobileLayout className="pb-36 pt-8 flex items-center justify-center min-h-screen relative">
        <MirrorBackButton className="absolute left-5 top-8" />


        <motion.div className="flex flex-col items-center gap-4 relative z-10"
          initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
          <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}>
            <Loader2 size={28} style={{ color: "#00e5ff" }} />
          </motion.div>
          <p className="text-[12px] font-display tracking-wide" style={{ color: "rgba(0,229,255,0.5)" }}>
            {t("nutrition.loading")}
          </p>
        </motion.div>
      </MobileLayout>
    );
  }

  // ── Error (inline banner, not full-page) ─────────────────────────────

  return (
    <MobileLayout className="pb-8 pt-8 relative overflow-hidden">
      <NutritionKeyframes />




      {/* Dark gradient base */}
      <div className="absolute inset-0 pointer-events-none z-0"
        style={{ background: "linear-gradient(180deg, #0a0a0f 0%, #0d1117 50%, #0a0a0f 100%)" }} />

      {/* Content */}
      <div className="relative z-10">

        {/* BACK */}
        <MirrorBackButton className="mb-5" />

        {/* HEADER (above Mini-Mirror per W3 visual fix) */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }} className="flex items-center justify-between mb-6">
          <div>
            <motion.p className="text-[9px] font-display tracking-[0.25em] uppercase mb-1"
              style={{ color: "rgba(0,229,255,0.4)" }}
              animate={{ opacity: [0.4, 0.8, 0.4] }} transition={{ duration: 4, repeat: Infinity }}>
              {t("nutrition.tagline")}
            </motion.p>
            <div className="flex items-center gap-2">
              <h1 className="font-display text-[28px] font-bold text-white leading-none tracking-tight">
                {t("module.nutrition.name")}
              </h1>
              <InfoTooltip title={t("tooltips.nutrition.title")} description={t("tooltips.nutrition.description")} iconClassName="text-white/70 hover:text-white" />
            </div>
          </div>
          <motion.div className="w-10 h-10 rounded-2xl flex items-center justify-center"
            style={{ background: "rgba(0,229,255,0.06)", border: "1px solid rgba(0,229,255,0.15)" }}
            animate={{ boxShadow: ["0 0 0px rgba(0,229,255,0)", "0 0 20px rgba(0,229,255,0.12)", "0 0 0px rgba(0,229,255,0)"] }}
            transition={{ duration: 5, repeat: Infinity }}>
            <Apple size={17} style={{ color: "#00e5ff" }} />
          </motion.div>
        </motion.div>

        {/* NUTRITION MINI-MIRROR — 3-part split clean-card (Brain MM template) */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05, duration: 0.5 }}
          className="mb-5"
        >
          <NutritionMiniMirror />
        </motion.div>

        {/* ERROR BANNER */}
        {error && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-xl p-3 mb-4 flex items-center justify-between"
            style={{
              background: "rgba(244,63,94,0.08)", border: "1px solid rgba(244,63,94,0.25)",
              backdropFilter: "blur(12px)",
            }}>
            <p className="text-[11px] font-body" style={{ color: "rgba(244,63,94,0.8)" }}>
              {t("nutrition.error")}
            </p>
            <motion.button onClick={reload} whileTap={{ scale: 0.92 }}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg font-display text-[10px] font-medium"
              style={{ background: "rgba(244,63,94,0.12)", color: "rgba(244,63,94,0.75)" }}>
              <RefreshCw size={10} /> {t("common.retry")}
            </motion.button>
          </motion.div>
        )}

        {!hasCustomGoals && <p className="text-sm text-muted-foreground mb-3">{t("nutrition.ux.referenceGoals")}</p>}
        {/* CALORIE HERO + MACROS */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="rounded-2xl p-5 mb-5 relative overflow-hidden"
          style={{
            background: "linear-gradient(135deg, rgba(26,26,46,0.85) 0%, rgba(15,15,30,0.95) 100%)",
            border: "1px solid rgba(0,229,255,0.12)", backdropFilter: "blur(20px)",
          }}>
          <div className="absolute top-0 left-0 right-0 h-[1px]"
            style={{ background: "linear-gradient(90deg, transparent, rgba(0,229,255,0.2), transparent)" }} />
          <p className="text-[10px] font-display tracking-[0.15em] uppercase mb-5 text-center"
            style={{ color: "hsl(187 72% 50% / 0.65)" }}>{t("nutrition.todaysMacros")}</p>
          <div className="flex justify-center mb-6">
            <CalorieArc current={todayMacros.kcal} target={targets.kcal} />
          </div>
          {todayMacros.kcal === 0 && (
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}
              className="text-[11px] font-body text-center mt-3 italic"
              style={{ color: "rgba(255,255,255,0.45)" }}
            >
              {t("nutrition.todayEmpty")}
            </motion.p>
          )}
          <div className="flex justify-around">
            <MacroRing value={todayMacros.protein} max={targets.protein} color="hsl(270 80% 65%)"
              glowColor="hsl(270 80% 65% / 0.25)" label={t("nutrition.protein")} unit="g" icon={<Zap size={15} />} />
            <MacroRing value={todayMacros.carbs} max={targets.carbs} color="hsl(200 80% 60%)"
              glowColor="hsl(200 80% 60% / 0.25)" label={t("nutrition.carbs")} unit="g" icon={<TrendingUp size={15} />} />
            <MacroRing value={todayMacros.fat} max={targets.fat} color="hsl(38 90% 58%)"
              glowColor="hsl(38 90% 58% / 0.25)" label={t("nutrition.fat")} unit="g" icon={<Droplets size={15} />} />
          </div>
        </motion.div>

        {/* PHOTO CTA — primary action (replaces logMealButton). Manual log
            available below as smaller fallback. */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5 }}
          className="flex flex-col items-center gap-3 mb-5"
        >
          <motion.button onClick={() => setShowPhotoCapture(true)} whileTap={{ scale: 0.94 }}
            className="w-full py-3.5 rounded-2xl font-display text-[14px] font-bold tracking-wide relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, #00e5ff, #00bcd4)",
              color: "#0a0a0f",
              boxShadow: "0 4px 30px rgba(0,229,255,0.35), 0 0 60px rgba(0,229,255,0.1)",
            }}>
            <motion.div className="absolute inset-0 rounded-2xl"
              animate={{ boxShadow: ["0 0 20px rgba(0,229,255,0.2)", "0 0 40px rgba(0,229,255,0.35)", "0 0 20px rgba(0,229,255,0.2)"] }}
              transition={{ duration: 3, repeat: Infinity }} />
            <motion.div className="absolute inset-0 opacity-20"
              style={{ background: "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.5) 50%, transparent 60%)" }}
              animate={{ x: ["-100%", "200%"] }}
              transition={{ duration: 4, repeat: Infinity, repeatDelay: 3 }} />
            <span className="relative z-10 flex flex-col items-center justify-center gap-0.5">
              <span className="flex items-center justify-center gap-2">
                <Camera size={15} strokeWidth={2.5} />
                {t("nutrition.photo.capture")}
              </span>
              <span className="text-[10px] font-medium opacity-75 tracking-normal">
                {t("nutrition.photo.subtext")}
              </span>
            </span>
          </motion.button>
          <button
            type="button"
            onClick={() => { setSelectedFood(null); setAutoStartVoice(false); setModal(true); }}
            className="text-[11px] font-body tracking-wide active:opacity-60 transition-opacity"
            style={{ color: "rgba(255,255,255,0.5)" }}
          >
            {t("nutrition.photo.or")} <span className="underline" style={{ color: "rgba(0,229,255,0.7)" }}>{t("nutrition.photo.use_manual")}</span>
          </button>
        </motion.div>

        {/* HYDRATION */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }} className="mb-5">
          <HydrationTracker cups={cups} goal={hydration.cups_goal} saving={isSaving} onAdd={handleAddWater} t={t} tf={tf} />
        </motion.div>

        {/* TODAY'S MEALS */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.6 }} className="mb-5">
          <div className="flex items-center gap-2 mb-3">
            <Utensils size={13} style={{ color: "hsl(187 72% 50% / 0.65)" }} />
            <p className="text-[10px] font-display tracking-[0.15em] uppercase"
              style={{ color: "hsl(187 72% 50% / 0.65)" }}>{t("nutrition.todaysMeals")}</p>
            <button
              onClick={openMealHistory}
              className="ml-auto flex items-center gap-1 font-display text-[10px] tracking-wide opacity-70 hover:opacity-100 transition-opacity"
              style={{ color: "hsl(187 72% 55%)" }}
            >
              <History size={11} />
              {t("nutrition.history.button")}
            </button>
          </div>
          {todayMeals.length === 0 ? (
            <motion.div className="rounded-2xl p-8 flex flex-col items-center relative overflow-hidden"
              style={{
                background: "linear-gradient(135deg, rgba(26,26,46,0.6) 0%, rgba(15,15,30,0.8) 100%)",
                border: "1px dashed rgba(0,229,255,0.15)",
              }}>
              <motion.div animate={{ y: [0, -4, 0] }} transition={{ duration: 3, repeat: Infinity }}>
                <Apple size={28} style={{ color: "hsl(187 72% 50% / 0.50)" }} />
              </motion.div>
              <p className="text-[12px] font-body mt-3 text-center" style={{ color: "rgba(255,255,255,0.50)" }}>
                {t("nutrition.noMeals")}
              </p>
              <motion.button onClick={() => setModal(true)} whileTap={{ scale: 0.95 }}
                className="mt-4 px-5 py-2.5 rounded-xl font-display text-[11px] font-medium"
                style={{ background: "rgba(0,229,255,0.08)", border: "1px solid rgba(0,229,255,0.25)", color: "rgba(0,229,255,0.70)", boxShadow: "0 0 12px rgba(0,229,255,0.08)" }}>
                {t("nutrition.logFirstMeal")}
              </motion.button>
            </motion.div>
          ) : (
            <div className="space-y-2.5">
              {todayMeals.map((meal, i) => (
                <MealCard key={meal.id} meal={meal} index={i} t={t}
                  onEdit={() => { setSelectedFood(null); setEditingMeal(meal); }}
                  onDelete={handleDeleteMeal} />
              ))}
            </div>
          )}
        </motion.div>

        {/* WEEKLY OVERVIEW */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }} className="mb-5">
          <WeeklyOverview weekData={weekData} t={t} />
        </motion.div>

        {/* EVOLUÇÃO — shared analytics dashboard (daily macro totals + trend +
            projection + meals-logged streak). Descriptive only — no prescription.
            Complements the WeeklyOverview (7-day % goal) with longer-range trend. */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.34, duration: 0.6 }} className="mb-5 rounded-2xl p-4"
          style={{ background: "hsl(var(--card))", border: "1px solid hsl(30 90% 55% / 0.15)" }}>
          <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-3" style={{ color: "hsl(30 90% 55% / 0.55)" }}>
            {t("nutrition.analytics.title")}
          </p>
          {historyError ? <div role="alert"><p>{t("nutrition.error")}</p><button onClick={() => void reloadMealHistory()}>{t("common.retry")}</button></div> : historyLoading && historyLogs.length === 0 ? <p role="status">{t("nutrition.loading")}</p> : <AnalyticsDashboard
            results={nutritionAnalytics}
            granularity={nutritionGran}
            onGranularityChange={setNutritionGran}
            emptyKey="nutrition.analytics.empty"
          />}
        </motion.div>

        {/* MIRROR NUTRITION BRIEF */}
        {/* TODO: Week 3 — Connect to Claude API mirror brief endpoint */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.6 }} className="mb-5">
          <MirrorInsightSlot titleKey="nutrition.mirrorBrief" fallbackKey="nutrition.mirrorBriefText"
            ctaKey="nutrition.planWithMirror" t={t} navigate={navigate}
            dynamicContent={
              isReported(REPORT_SURFACES.REACTION_NUTRITION, reactedMealId) ? null : mirrorReact.reaction
            }
            isLoadingAI={mirrorReact.isLoading}
            reportContentId={reactedMealId} />
        </motion.div>

        {/* MIRROR INSIGHT */}
        {/* TODO: Week 3 — Connect to Claude API mirror insight endpoint */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.6 }} className="pb-24">
          <MirrorInsightSlot titleKey="nutrition.mirrorInsight" fallbackKey="nutrition.mirrorInsightText"
            ctaKey="nutrition.discuss" t={t} navigate={navigate} />
        </motion.div>
      </div>

      {/* MEAL HISTORY OVERLAY — chronological, per-record edit/delete+undo */}
      <AnimatePresence>
        {historyOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-end justify-center"
            style={{ background: "rgba(5,5,12,0.82)", backdropFilter: "blur(8px)" }}
            onClick={() => setHistoryOpen(false)}>
            <motion.div
              initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 300 }}
              className="w-full max-w-md rounded-t-3xl flex flex-col"
              style={{
                background: "hsl(220 25% 6%)",
                border: "1px solid rgba(0,229,255,0.15)", borderBottom: "none",
                maxHeight: "88dvh",
                overscrollBehavior: "contain",
              }}
              onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-5 pt-5 pb-3 shrink-0">
                <h2 className="font-display text-[15px] font-semibold text-white">
                  {t("nutrition.history.title")}
                </h2>
                <button onClick={() => setHistoryOpen(false)} aria-label={t("common.close")}
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ background: "hsl(222 18% 12%)", border: "1px solid hsl(222 18% 20%)" }}>
                  <X size={14} className="text-white" />
                </button>
              </div>
              <div className="px-5 pb-8 overflow-y-auto">
                <p className="text-sm text-muted-foreground mb-3">{t("nutrition.ux.historyScope")}</p>
                {historyError && <div role="alert" className="mb-3"><p>{t("nutrition.error")}</p><button onClick={() => void reloadMealHistory()}>{t("common.retry")}</button></div>}
                {/* Spinner only on FIRST load — reloads keep the list mounted
                    so an open undo window is never unmount-flushed. */}
                {historyLoading && historyLogs.length === 0 ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 size={20} className="animate-spin" style={{ color: "#00e5ff" }} />
                  </div>
                ) : !historyError || historyLogs.length > 0 ? (
                  <ModuleHistoryList<NutritionLog>
                    items={historyLogs}
                    getId={(l) => l.id}
                    getDate={(l) => l.date + "T12:00:00"}
                    renderItem={(l) => {
                      const mt = ALL_MEAL_TYPES.find((m) => m.key === l.meal_type);
                      return (
                        <div className="min-w-0">
                          <p className="text-sm text-white truncate">
                            {(mt ? mt.emoji + " " : "") + (l.description || (mt ? t(mt.labelKey) : ""))}
                          </p>
                          <p className="text-[11px] text-muted-foreground mt-0.5">
                            {Math.round(Number(l.kcal))} kcal · P {Math.round(Number(l.protein))}g · C {Math.round(Number(l.carbs))}g · G {Math.round(Number(l.fat))}g
                          </p>
                        </div>
                      );
                    }}
                    onEdit={(l) => setEditingMeal({
                      id: l.id,
                      meal_type: l.meal_type,
                      description: l.description,
                      kcal: Number(l.kcal),
                      protein: Math.round(Number(l.protein)),
                      carbs: Math.round(Number(l.carbs)),
                      fat: Math.round(Number(l.fat)),
                    })}
                    onDelete={async (l) => {
                      try {
                        await deleteLog(l.id);
                        reloadMealHistory();
                      } catch {
                        toast.error(t("nutrition.log_error"));
                        throw new Error("Meal deletion failed");
                      }
                    }}
                    labels={{
                      empty: t("nutrition.history.empty"),
                      deletedToast: t("moduleHistory.deletedToast"),
                      undo: t("moduleHistory.undo"),
                      period30: t("moduleHistory.period30"),
                      period90: t("moduleHistory.period90"),
                      periodAll: t("moduleHistory.periodAll"),
                    }}
                  />
                ) : null}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* LOG MEAL MODAL */}
      <AnimatePresence>
        {modal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-end justify-center"
            style={{ background: "rgba(5,5,12,0.82)", backdropFilter: "blur(8px)", WebkitOverflowScrolling: "touch" as any }}
            onClick={() => { if (!captureLock.current) setModal(false); }}>
            <motion.div
              initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 300 }}
              className="w-full max-w-md rounded-t-3xl flex flex-col"
              style={{
                background: "hsl(220 25% 6%)",
                border: "1px solid rgba(0,229,255,0.15)", borderBottom: "none",
                maxHeight: "92dvh",
                boxShadow: "0 -10px 60px rgba(0,229,255,0.08), inset 0 1px 0 hsl(187 72% 50% / 0.20)",
                overscrollBehavior: "contain",
              }}
              onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-center pt-3 pb-1">
                <div className="w-12 h-1 rounded-full" style={{ background: "hsl(187 72% 50% / 0.30)" }} />
              </div>
              <div className="flex items-center justify-between px-6 pt-3 pb-2 shrink-0">
                <div>
                  <p className="font-display text-lg font-semibold text-white">{t("nutrition.logMeal")}</p>
                  <p className="text-[10px] font-body mt-0.5" style={{ color: "rgba(255,255,255,0.3)" }}>
                    {t("nutrition.log_subtitle")}
                  </p>
                </div>
                <motion.button disabled={captureSaving} aria-label={t("common.close")} onClick={() => { if (!captureLock.current) setModal(false); }} whileTap={{ scale: 0.85 }}
                  className="w-8 h-8 rounded-xl flex items-center justify-center"
                  style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                  <X size={16} style={{ color: "rgba(255,255,255,0.4)" }} />
                </motion.button>
              </div>
              <QuickLogForm
                onSubmit={handleLogMeal}
                onSavingChange={(saving) => { captureLock.current = saving; setCaptureSaving(saving); }}
                onClose={() => { setModal(false); setAutoStartVoice(false); }}
                t={t}
                autoStartVoice={autoStartVoice}
                onOpenFoodSearch={handleOpenFoodSearch}
                selectedFood={selectedFood}
                recentDescriptions={recentDescriptions}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>


      {/* FoodSearchScreen — rendered at ROOT level, outside modal DOM */}
      <AnimatePresence>
        {showFoodSearch && (
          <FoodSearchScreen
            initialQuery={foodSearchInitialQuery}
            onSelect={handleFoodSelect}
            onClose={() => setShowFoodSearch(false)}
          />
        )}
      </AnimatePresence>

      {/* EditMealModal — rendered at ROOT level */}
      <AnimatePresence>
        {editingMeal && (
          <EditMealModal
            meal={editingMeal}
            onSave={handleUpdateMeal}
            onClose={() => setEditingMeal(null)}
            t={t}
            onOpenFoodSearch={handleOpenFoodSearch}
            selectedFood={selectedFood}
          />
        )}
      </AnimatePresence>

      {/* PhotoCaptureScreen — rendered at ROOT level (full-screen overlay) */}
      <AnimatePresence>
        {showPhotoCapture && (
          <PhotoCaptureScreen
            onClose={() => setShowPhotoCapture(false)}
            onLogged={() => { setShowPhotoCapture(false); reload(); }}
          />
        )}
      </AnimatePresence>
    </MobileLayout>
  );
};

const readNutritionIdentity = async () => true;
const Nutrition: React.FC = () => {
  const identity = useNutritionResource(readNutritionIdentity);
  const { t } = useLanguage();
  const navigate = useNavigate();
  if (!identity.userId) return <MobileLayout className="p-6 min-h-screen"><MirrorBackButton />
    <p role={identity.status === "error" ? "alert" : "status"}>{t(identity.status === "error" ? "nutrition.error" : identity.status === "signed-out" ? "nutrition.ux.signIn" : "nutrition.loading")}</p>
    {identity.status === "error" && <button onClick={() => void identity.refresh()}>{t("common.retry")}</button>}
    {identity.status === "signed-out" && <button onClick={() => navigate("/auth?mode=signin", { state: { from: "/nutrition" } })}>{t("nutrition.ux.signIn")}</button>}
  </MobileLayout>;
  return <NutritionContent key={identity.userId} />;
};
export default Nutrition;
